import { VehicleDetails } from '@/features/vehicles/components'
import { useVehicle, vehicleDetailsDataQueryOptions } from '@/features/vehicles/hooks'
import { queryClient } from '@/utils/query-client.ts'
import { Alert, Button, CircularProgress, Typography } from '@mui/material'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useCompany } from '@/contexts'
import { ApiErrorAlertBox } from '@/components/ApiAlertBox/ApiErrorAlertBox.tsx'

function ErrorComponent({ error }: { error: Error }) {
  const { t } = useTranslation('_global')
  const router = useRouter()
  const queryErrorResetBoundary = useQueryErrorResetBoundary()

  useEffect(() => {
    // Reset the query error boundary
    queryErrorResetBoundary.reset()
  }, [queryErrorResetBoundary])

  return (
    <div>
      <Alert severity="warning">
        <Typography variant="body2">
          {t(`api.message.errors.${error.message}`, {
            defaultValue: error.cause as string
          })}
        </Typography>
      </Alert>
      <Button variant="outlined" onClick={() => router.history.back()}>
        Go Back
      </Button>
      <Button
        sx={{ my: 2, mx: 2 }}
        variant="contained"
        onClick={() => {
          // Invalidate the route to reload the loader, and reset any router error boundaries
          router.invalidate()
        }}
      >
        Retry
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/$companySlug/vehicles/$vin/')({
  beforeLoad: ({ context }) => {
    context.auth.rejectIfMissingPermissions(['VEHICLE_READ'])
  },
  component: Index,
  loader: ({ params }) =>
    queryClient.ensureQueryData(vehicleDetailsDataQueryOptions(params.vin)),
  head: data => ({
    meta: [{ title: `Vehicle ${data.loaderData?.licensePlate ?? data.params.vin}` }]
  }),
  errorComponent: ErrorComponent
})

function Index() {
  const { sites } = useCompany()
  const navigate = Route.useNavigate()
  const { vin } = Route.useParams()

  const vehicleResponse = useVehicle(vin)

  // Find the site associated with the vehicle
  const vehicleSite = sites.find(site => site.id === vehicleResponse.data?.siteId)
  const basicSite = vehicleSite
    ? { id: vehicleSite.id, name: vehicleSite.name }
    : undefined

  const handleNavigateToUpdatePage = () => {
    navigate({
      to: '/$companySlug/vehicles/$vin/update',
      params: { vin }
    })
  }

  return (
    <>
      {!vehicleResponse.error && basicSite ? (
        <VehicleDetails
          vehicle={vehicleResponse.data!}
          site={basicSite}
          onManageRoutesClick={handleNavigateToUpdatePage}
        />
      ) : (
        <CircularProgress />
      )}

      <ApiErrorAlertBox error={vehicleResponse.error} />
    </>
  )
}
